import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/db8idi9rh5tg6socjup0";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, String(value)));

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
        return;
      }

      setStatus("success");
      setFormError("");
      formEl.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      id="manwa2-feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            昵称
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你？"
            className="h-10 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于我们回复你"
            className="h-10 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="fb-subject"
          name="subject"
          defaultValue="内容建议"
          className="h-10 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 outline-none focus:border-accent-400"
        >
          <option value="内容建议">内容建议</option>
          <option value="功能反馈">功能反馈</option>
          <option value="内容纠错">内容纠错</option>
          <option value="合作咨询">合作咨询</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请描述你遇到的问题或建议（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-200 bg-background-100 p-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400"
        />
      </div>

      <input
        className="nw-hp-alt"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="ri-send-plane-line"></i>
          {status === "submitting" ? "提交中..." : "提交反馈"}
        </button>
        <span className="text-xs text-foreground-500">我们通常在 1-2 个工作日内回复。</span>
      </div>

      {status === "success" && (
        <div className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-checkbox-circle-line"></i>
          提交成功，感谢你的反馈！我们会尽快查看。
        </div>
      )}
      {status === "error" && (
        <div className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-error-warning-line"></i>
          {formError || "提交失败，请稍后再试。"}
        </div>
      )}
    </form>
  );
}